/**
 * Machine Setup Repository
 * Data access layer for machine-setup feature
 * Wraps API calls with descriptive error re-throwing
 */

import { MachineSetupApi } from './machine-setup-api';
import type {
  MachineTypesResponse,
  MachineGroupsResponse,
  SerialValidationResponse,
  CreateMachineRequest,
  CreateMachineResponse,
  ValidateMachineConfigResponse,
  MachineTypeSettingsResponse,
  SaveTypeSettingsRequest,
  AddCustomMachineTypeRequest,
  CreateThirdPartyMachineRequest,
  ThirdPartyManufacturer,
  DeviceSuggestion,
  LynxMachineInfoResponse,
  ApplyAttributeItem,
  BulkCreateMachineRequest,
  BulkCreateMachineResponse,
} from '../types/machine-setup-types';

export class MachineSetupRepository {
  constructor(private readonly api: MachineSetupApi) {}

  async getMachineTypes(): Promise<MachineTypesResponse> {
    try {
      return await this.api.getMachineTypes();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch machine types';
      throw new Error(message);
    }
  }

  async validateSerial(serialNumber: string): Promise<SerialValidationResponse> {
    try {
      return await this.api.validateSerial(serialNumber);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to validate serial number';
      throw new Error(message);
    }
  }

  async createMachine(request: CreateMachineRequest): Promise<CreateMachineResponse> {
    try {
      return await this.api.createMachine(request);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create machine';
      throw new Error(message);
    }
  }

  async validateMachineConfig(
    machineId: number,
    machineTypeKey: string
  ): Promise<ValidateMachineConfigResponse> {
    try {
      return await this.api.validateMachineConfig(machineId, machineTypeKey);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to validate machine config';
      throw new Error(message);
    }
  }

  async getMachineGroups(actorId: number): Promise<MachineGroupsResponse> {
    try {
      return await this.api.getMachineGroups(actorId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch machine groups';
      throw new Error(message);
    }
  }

  async getActorsForOperator(operatorId: number): Promise<Array<{ actorId: number; name: string; parentActorId?: number }>> {
    try {
      return await this.api.getActorsForOperator(operatorId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch actors for operator';
      throw new Error(message);
    }
  }

  async getDevicesForActor(actorId: number): Promise<DeviceSuggestion[]> {
    try {
      return await this.api.getDevicesForActor(actorId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch devices for actor';
      throw new Error(message);
    }
  }

  async getMachineInfo(machineId: number): Promise<LynxMachineInfoResponse> {
    try {
      return await this.api.getMachineInfo(machineId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch machine info';
      throw new Error(message);
    }
  }

  async applyAttributes(machineId: number, attributes: ApplyAttributeItem[]): Promise<{ isSuccess: boolean; message: string | null }> {
    try {
      return await this.api.applyAttributes(machineId, attributes);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to apply attributes';
      throw new Error(message);
    }
  }

  async getTypeSettings(): Promise<MachineTypeSettingsResponse> {
    try {
      return await this.api.getTypeSettings();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch type settings';
      throw new Error(message);
    }
  }

  async saveTypeSettings(machineTypeKey: string, data: SaveTypeSettingsRequest): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    try {
      return await this.api.saveTypeSettings(machineTypeKey, data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save type settings';
      throw new Error(message);
    }
  }

  async addCustomMachineType(data: AddCustomMachineTypeRequest): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    try {
      return await this.api.addCustomMachineType(data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add custom machine type';
      throw new Error(message);
    }
  }

  async deleteCustomMachineType(machineTypeKey: string): Promise<{ isSuccess: boolean; errorMessage?: string }> {
    try {
      return await this.api.deleteCustomMachineType(machineTypeKey);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete custom machine type';
      throw new Error(message);
    }
  }

  async getManufacturers(): Promise<ThirdPartyManufacturer[]> {
    try {
      return await this.api.getManufacturers();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch manufacturers';
      throw new Error(message);
    }
  }

  async createThirdPartyMachine(request: CreateThirdPartyMachineRequest): Promise<CreateMachineResponse> {
    try {
      return await this.api.createThirdPartyMachine(request);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create third-party machine';
      throw new Error(message);
    }
  }

  async createMachineBulk(request: BulkCreateMachineRequest): Promise<BulkCreateMachineResponse> {
    try {
      return await this.api.createMachineBulk(request);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create machines in bulk';
      throw new Error(message);
    }
  }
}
